(function () {
  'use strict';

  /*
    Reusable hover water-ripple overlay. Sets up every [data-ripple] host with a
    per-host height-field water sim rendered to an injected <canvas class="c-ripple">.
    Two render modes (mode = host[data-ripple-mode]), each with its own preset:
      sheen   (default) translucent tonal glint over the host's own background
      refract bilinear-samples a [data-ripple-src] image/video, bent by the waves
    Hover-only and lazy: nothing is allocated until the first pointerenter, and
    the loop self-halts once the surface settles and the pointer has left, so idle
    hosts cost ~nothing (important when many cards opt in). Skipped under
    prefers-reduced-motion. See component-common-ripple.css for layering.
  */

  const PRESETS = {
    // Small UI surfaces (buttons): subtle tonal glint, quick settle.
    sheen: {
      edge: 180,            // sim grid longest side (lower = softer on small hosts)
      damping: 0.96,        // 0.90 choppy .. 0.98 glassy
      sheen: 6,             // wave slope -> sheen alpha
      sheenMax: 80,         // cap on sheen alpha (0..255)
      displace: 0,
      shade: 0,
      move: { radius: 4, strength: 25, minDist: 16 },
      click: { radius: 8, strength: 180 }
    },
    // Larger image surfaces (cards): bends the image with a soft glint.
    refract: {
      edge: 480,            // more detail before the CSS upscale
      damping: 0.96,        // ~1.6s lifetime
      sheen: 0,
      sheenMax: 0,
      displace: 0.8,        // how far the water bends the image
      shade: 0.7,           // soft sheen on the wave slopes
      move: { radius: 8, strength: 50, minDist: 10 },
      click: { radius: 8, strength: 260 }
    }
  };

  function clamp255(v) {
    return v < 0 ? 0 : v > 255 ? 255 : v;
  }

  function setup(host) {
    if (host.dataset.rippleInit) {
      return; // already wired (e.g. if the script is included more than once)
    }
    host.dataset.rippleInit = '1';

    const mode = host.getAttribute('data-ripple-mode') === 'refract' ? 'refract' : 'sheen';
    const cfg = PRESETS[mode];

    // Pointer events are listened for on the host by default, but a host can
    // delegate to an ancestor via data-ripple-trigger="<selector>" when something
    // (e.g. an overlay link) sits on top and would otherwise eat the events.
    const triggerSel = host.getAttribute('data-ripple-trigger');
    const trigger = triggerSel ? (host.closest(triggerSel) || host) : host;

    const canvas = document.createElement('canvas');
    canvas.className = 'c-ripple';
    canvas.setAttribute('aria-hidden', 'true');
    host.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    let media = null;
    let isVideo = false;
    let surface = null;
    let srcData = null;
    if (mode === 'refract') {
      media = host.querySelector('[data-ripple-src]');
      if (media) {
        isVideo = media.tagName === 'VIDEO';
        surface = document.createElement('canvas');
      }
    }

    const state = {
      W: 0, H: 0, prev: null, curr: null, out: null,
      raf: null, hovering: false, lastX: 0, lastY: 0, ready: false
    };

    function buildSource() {
      if (mode !== 'refract' || !media || !surface || !state.W) {
        return;
      }
      const mw = isVideo ? media.videoWidth : media.naturalWidth;
      const mh = isVideo ? media.videoHeight : media.naturalHeight;
      if (!mw || !mh) {
        srcData = null;
        return;
      }
      surface.width = state.W;
      surface.height = state.H;
      const c = surface.getContext('2d');
      const ir = mw / mh;
      const cr = state.W / state.H;
      let dw;
      let dh;
      if (ir > cr) { dh = state.H; dw = state.H * ir; } else { dw = state.W; dh = state.W / ir; }
      c.drawImage(media, (state.W - dw) / 2, (state.H - dh) / 2, dw, dh);
      try {
        srcData = c.getImageData(0, 0, state.W, state.H).data;
      } catch {
        srcData = null; // tainted (no CORS) -> refract render is skipped
      }
    }

    function resize() {
      const r = host.getBoundingClientRect();
      if (!r.width || !r.height) {
        return;
      }
      const aspect = r.width / r.height;
      if (aspect >= 1) {
        state.W = cfg.edge;
        state.H = Math.max(1, Math.round(cfg.edge / aspect));
      } else {
        state.H = cfg.edge;
        state.W = Math.max(1, Math.round(cfg.edge * aspect));
      }
      canvas.width = state.W;
      canvas.height = state.H;
      state.prev = new Float32Array(state.W * state.H);
      state.curr = new Float32Array(state.W * state.H);
      state.out = ctx.createImageData(state.W, state.H);
      buildSource();
      ctx.clearRect(0, 0, state.W, state.H);
    }

    // Lazy: allocate buffers on first interaction so idle hosts cost nothing.
    function ensure() {
      if (!state.ready) {
        resize();
        state.ready = !!state.W;
      }
    }

    function drop(clientX, clientY, radius, strength) {
      const r = canvas.getBoundingClientRect();
      const cx = Math.floor((clientX - r.left) / r.width * state.W);
      const cy = Math.floor((clientY - r.top) / r.height * state.H);
      const prev = state.prev;
      const W = state.W;
      const H = state.H;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const d2 = dx * dx + dy * dy;
          if (d2 > radius * radius) {
            continue;
          }
          const x = cx + dx;
          const y = cy + dy;
          if (x > 0 && x < W - 1 && y > 0 && y < H - 1) {
            prev[x + y * W] += strength * (1 - d2 / (radius * radius));
          }
        }
      }
    }

    function renderSheen() {
      const prev = state.prev;
      const W = state.W;
      const H = state.H;
      const data = state.out.data;
      const maxX = W - 1;
      for (let y = 0; y < H; y++) {
        const row = y * W;
        for (let x = 0; x < W; x++) {
          const i = row + x;
          const g = (x > 0 && x < maxX) ? (prev[i - 1] - prev[i + 1]) * cfg.sheen : 0;
          let a = g < 0 ? -g : g;
          if (a > cfg.sheenMax) {
            a = cfg.sheenMax;
          }
          const lit = g >= 0 ? 255 : 0;
          const di = i * 4;
          data[di] = lit;
          data[di + 1] = lit;
          data[di + 2] = lit;
          data[di + 3] = a;
        }
      }
      ctx.putImageData(state.out, 0, 0);
    }

    function renderRefract() {
      if (!srcData) {
        return;
      }
      const prev = state.prev;
      const W = state.W;
      const H = state.H;
      const data = state.out.data;
      const maxX = W - 1;
      const maxY = H - 1;
      for (let y = 0; y < H; y++) {
        const row = y * W;
        for (let x = 0; x < W; x++) {
          const i = row + x;
          let xo = 0;
          let yo = 0;
          if (x > 0 && x < maxX && y > 0 && y < maxY) {
            xo = (prev[i - 1] - prev[i + 1]) * cfg.displace;
            yo = (prev[i - W] - prev[i + W]) * cfg.displace;
          }
          let fx = x + xo;
          if (fx < 0) { fx = 0; } else if (fx > maxX) { fx = maxX; }
          let fy = y + yo;
          if (fy < 0) { fy = 0; } else if (fy > maxY) { fy = maxY; }
          const x0 = fx | 0;
          const y0 = fy | 0;
          const x1 = x0 < maxX ? x0 + 1 : x0;
          const y1 = y0 < maxY ? y0 + 1 : y0;
          const tx = fx - x0;
          const ty = fy - y0;
          const i00 = (x0 + y0 * W) * 4;
          const i10 = (x1 + y0 * W) * 4;
          const i01 = (x0 + y1 * W) * 4;
          const i11 = (x1 + y1 * W) * 4;
          const glint = xo * cfg.shade;
          const di = i * 4;
          for (let ch = 0; ch < 3; ch++) {
            const top = srcData[i00 + ch] + (srcData[i10 + ch] - srcData[i00 + ch]) * tx;
            const bot = srcData[i01 + ch] + (srcData[i11 + ch] - srcData[i01 + ch]) * tx;
            data[di + ch] = clamp255(top + (bot - top) * ty + glint);
          }
          data[di + 3] = 255;
        }
      }
      ctx.putImageData(state.out, 0, 0);
    }

    function loop() {
      const prev = state.prev;
      const curr = state.curr;
      const W = state.W;
      const H = state.H;
      let energy = 0;
      for (let y = 1; y < H - 1; y++) {
        const row = y * W;
        for (let x = 1; x < W - 1; x++) {
          const i = row + x;
          const v = ((prev[i - 1] + prev[i + 1] + prev[i - W] + prev[i + W]) * 0.5 - curr[i]) * cfg.damping;
          curr[i] = v;
          energy += v < 0 ? -v : v;
        }
      }
      state.prev = curr;
      state.curr = prev;

      if (mode === 'refract') {
        if (isVideo) {
          buildSource();
        }
        renderRefract();
      } else {
        renderSheen();
      }

      if (state.hovering || energy > 0.5) {
        state.raf = requestAnimationFrame(loop);
      } else {
        state.raf = null;
        ctx.clearRect(0, 0, state.W, state.H);
      }
    }

    function start() {
      if (!state.raf && state.ready) {
        state.raf = requestAnimationFrame(loop);
      }
    }

    trigger.addEventListener('pointerenter', function (e) {
      ensure();
      state.hovering = true;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      drop(e.clientX, e.clientY, cfg.move.radius, cfg.move.strength);
      start();
    });
    trigger.addEventListener('pointermove', function (e) {
      if (!state.hovering || !state.ready) {
        return;
      }
      const dx = e.clientX - state.lastX;
      const dy = e.clientY - state.lastY;
      if (dx * dx + dy * dy < cfg.move.minDist * cfg.move.minDist) {
        return;
      }
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      drop(e.clientX, e.clientY, cfg.move.radius, cfg.move.strength);
      start();
    });
    trigger.addEventListener('pointerdown', function (e) {
      ensure();
      drop(e.clientX, e.clientY, cfg.click.radius, cfg.click.strength);
      start();
    });
    trigger.addEventListener('pointerleave', function () {
      state.hovering = false;
    });

    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(function () {
        if (state.ready) {
          resize();
        }
      });
      ro.observe(host);
    } else {
      window.addEventListener('resize', function () {
        if (state.ready) {
          resize();
        }
      });
    }
  }

  function init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const hosts = document.querySelectorAll('[data-ripple]');
    for (let i = 0; i < hosts.length; i++) {
      setup(hosts[i]);
    }
  }

  document.addEventListener('DOMContentLoaded', init);
})();
